import dateUtil from '../../../utils/dateUtil';
import { Employee } from '../../../hooks/useEmployeeData';
import { Absence, AbsenceStatus } from '../services/AbsenceService';

export type SortColumn = 'employee_name' | 'status' | 'requested_on' | 'startEnd';
export type SortDirection = 'asc' | 'desc';

export function getEmpName(abs: Absence, allEmployees: Employee[]): string {
  if (abs.employee_name && abs.employee_name.trim()) {
    return abs.employee_name;
  }
  const found = allEmployees.find(e => e.id === abs.employee);
  if (!found) return '???';
  return `${found.first_name} ${found.last_name}`;
}

export function getRequestedOnSource(abs: Absence): string {
  return (abs as any).requested_on
    || (abs as any).requested_at
    || (abs as any).request_date
    || (abs as any).request_on
    || (abs as any).requested
    || (abs as any).created_at
    || (abs as any).created_on
    || (abs as any).createdOn
    || (abs as any).created
    || (abs as any).submitted_on
    || (abs as any).submitted_at
    || '';
}

export function sortAbsences(
  list: Absence[],
  sortBy: SortColumn,
  sortDir: SortDirection,
  allEmployees: Employee[],
): Absence[] {
  const strCompare = (valA: string, valB: string): number => {
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  };

  const newList = [...(list || [])];
  newList.sort((a, b) => {
    const nameA = getEmpName(a, allEmployees).toLowerCase();
    const nameB = getEmpName(b, allEmployees).toLowerCase();

    switch (sortBy) {
      case 'employee_name':
        return strCompare(nameA, nameB);
      case 'status':
        return strCompare((a.status || '').toLowerCase(), (b.status || '').toLowerCase());
      case 'requested_on':
        return strCompare(getRequestedOnSource(a), getRequestedOnSource(b));
      case 'startEnd':
        return strCompare(a.start_datetime || '', b.start_datetime || '');
      default:
        return 0;
    }
  });
  return newList;
}

export function filterAbsences(
  absences: Absence[],
  selectedWorkAreas: number[],
  activeEmployeeId: number | null,
  dateRange: { start: Date; end: Date } | null,
  statusFilter: Record<AbsenceStatus, boolean>,
  allEmployees: Employee[],
): Absence[] {
  return absences.filter(abs => {
    if (selectedWorkAreas.length > 0) {
      let empWorkAreas = abs.employee_work_areas;
      if (!empWorkAreas || empWorkAreas.length === 0) {
        const emp = allEmployees.find(e => e.id === abs.employee);
        empWorkAreas = emp?.work_areas || [];
      }
      if (!empWorkAreas || empWorkAreas.length === 0) return false;
      const overlap = empWorkAreas.filter(waId =>
        selectedWorkAreas.includes(waId)
      );
      if (overlap.length === 0) return false;
    }
    if (activeEmployeeId && abs.employee !== activeEmployeeId) {
      return false;
    }
    if (dateRange) {
      const start = new Date(abs.start_datetime);
      const end = new Date(abs.end_datetime);
      if (!(end >= dateRange.start && start < dateRange.end)) return false;
    }
    const anyChecked = Object.values(statusFilter).some(Boolean);
    if (anyChecked && !statusFilter[abs.status]) return false;
    return true;
  });
}

export function formatReqOnShort(abs: Absence): string {
  const reqSource = getRequestedOnSource(abs);
  if (!reqSource) return '\u2014';
  const dt = new Date(reqSource);
  const dd = dateUtil.pad2(dt.getDate());
  const mm = dateUtil.pad2(dt.getMonth() + 1);
  const yy = String(dt.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
}

export function formatStartEnd(abs: Absence): string {
  const isFull = !!(abs as any)?.full_day;
  const apiStart = (abs as any)?.start_date;
  const apiEnd = (abs as any)?.end_date;

  const parseYMD = (ymd: string): Date => {
    const [y, m, d] = String(ymd).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const fmtShortDE = (d: Date): string => {
    if (!d || isNaN(d.getTime())) return '';
    const weekday = d.toLocaleDateString('de-DE', { weekday: 'short' });
    const dd = dateUtil.pad2(d.getDate());
    const mm = dateUtil.pad2(d.getMonth() + 1);
    return `${weekday}, ${dd}.${mm}.`;
  };

  return (isFull && apiStart && apiEnd)
    ? `${fmtShortDE(parseYMD(apiStart))} - ${fmtShortDE(parseYMD(apiEnd))}`
    : `${fmtShortDE(dateUtil.createLocalDate(abs.start_datetime))} - ${fmtShortDE(dateUtil.createLocalDate(abs.end_datetime))}`;
}

export function buildPayloadFromTimes(
  data: { start_date: string; full_day_start: boolean; start_day_start_time: string; start_day_end_time: string;
          end_date: string; full_day_end: boolean; end_day_start_time: string; end_day_end_time: string }
): { finalStart: string; finalEnd: string; isFullDay: boolean } {
  let finalStart = '', finalEnd = '';
  if (data.start_date) {
    if (data.full_day_start) finalStart = `${data.start_date}T00:00:00`;
    else { const times = [data.start_day_start_time, data.start_day_end_time].sort(); finalStart = `${data.start_date}T${times[0]}:00`; }
  }
  if (data.end_date) {
    if (data.full_day_end) finalEnd = `${data.end_date}T23:59:59`;
    else { const times = [data.end_day_start_time, data.end_day_end_time].sort(); finalEnd = `${data.end_date}T${times[1]}:00`; }
  }
  return { finalStart, finalEnd, isFullDay: data.full_day_start && data.full_day_end };
}

export function computeDayCount(abs: Absence): number {
  if ((abs as any)?.days !== undefined && (abs as any)?.days !== null) {
    return (abs as any).days;
  }
  const isFull = !!(abs as any)?.full_day;
  const apiStart = (abs as any)?.start_date;
  const apiEnd = (abs as any)?.end_date;
  if (isFull && apiStart && apiEnd) {
    const parseYMD = (ymd: string): Date => {
      const [y, m, d] = String(ymd).split('-').map(Number);
      return new Date(y, (m || 1) - 1, d || 1);
    };
    const sd = parseYMD(apiStart);
    const ed = parseYMD(apiEnd);
    const diffDays = Math.floor((ed.getTime() - sd.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    return Math.max(diffDays, 0);
  }
  return 1;
}
